import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Button, Row, TextField } from 'seagull';
import { RichTextEditor } from 'seagull/components/RichTextEditor/RichTextEditor';
import { isEmptyRichText, isEmptyRichTextString } from 'seagull/utils/richText';
import { getTestIds } from 'seagull/utils/TestIds';
import { DisplayScopeCheckbox } from '~/components/DisplayScopeCheckbox/DisplayScopeCheckbox';
import { useStore } from '~/contexts';
import { useCreateCustomPageTemplate } from '~/hooks/reactQuery/mutations/useCreateCustomPageTemplate';
import { useTemplateSaveErrorHandler } from '~/hooks/reactQuery/mutations/useTemplateSaveErrorHandler';
import { useUpdateCustomPageTemplate } from '~/hooks/reactQuery/mutations/useUpdateCustomPageTemplate';
import { useCustomPageTemplates } from '~/hooks/reactQuery/queries/useCustomPageTemplates';
import { useLocaleKeys } from '~/i18n';
import { isDuplicateTemplateName } from '~/models/custom-page-template/isDuplicateTemplateName';
import { selectors } from '~/store/state-selectors';
import s from './TextTemplateFormModal.module.scss';
export const GetTextTemplateFormModalTID = (parentTestId)=>getTestIds(parentTestId);
const serializeEditorContent = (content)=>content ? JSON.stringify(content) : '';
export const TextTemplateForm = (param)=>{
    let { mode, template, onBack, onSaved } = param;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetTextTemplateFormModalTID(), []);
    const businessId = useStore(selectors.user.businessId);
    const { templates: existingTemplates } = useCustomPageTemplates();
    const [name, setName] = useState(mode === 'edit' && template ? template.name : '');
    const [pageTitle, setPageTitle] = useState(mode === 'edit' && template ? template.pageTitle : '');
    const [displayScope, setDisplayScope] = useState(mode === 'edit' && template ? template.displayScope : 'proposalOnly');
    const initialEditorValue = mode === 'edit' && template ? template.text.content : '';
    const editorContentRef = useRef(undefined);
    const [nameError, setNameError] = useState(undefined);
    const [pageTitleError, setPageTitleError] = useState(undefined);
    const [contentError, setContentError] = useState(undefined);
    const { createTemplate, isLoading: isCreateLoading } = useCreateCustomPageTemplate({
        onSuccess: ()=>onSaved()
    });
    const { updateTemplate, isLoading: isUpdateLoading } = useUpdateCustomPageTemplate({
        onSuccess: ()=>onSaved()
    });
    const handleSaveError = useTemplateSaveErrorHandler({
        mode,
        setNameError
    });
    const isLoading = isCreateLoading || isUpdateLoading;
    const handleEditorChange = useCallback((value)=>{
        editorContentRef.current = value;
        if (contentError && !isEmptyRichText(value)) setContentError(undefined);
    }, [
        contentError
    ]);
    const isEditorContentEmpty = ()=>{
        if (editorContentRef.current != null) {
            return isEmptyRichText(editorContentRef.current);
        }
        return isEmptyRichTextString(initialEditorValue);
    };
    const validate = ()=>{
        let valid = true;
        const trimmedName = name.trim();
        if (trimmedName === '') {
            setNameError(t.customPages.templates.fieldRequired());
            valid = false;
        } else if (isDuplicateTemplateName(trimmedName, existingTemplates, template === null || template === void 0 ? void 0 : template.id)) {
            setNameError(t.customPages.templates.duplicateNameMessage());
            valid = false;
        } else {
            setNameError(undefined);
        }
        if (pageTitle.trim() === '') {
            setPageTitleError(t.customPages.templates.fieldRequired());
            valid = false;
        } else {
            setPageTitleError(undefined);
        }
        if (isEditorContentEmpty()) {
            setContentError(t.customPages.templates.fieldRequired());
            valid = false;
        } else {
            setContentError(undefined);
        }
        return valid;
    };
    const handleSave = ()=>{
        if (isLoading) return;
        if (!validate()) return;
        const serializedContent = editorContentRef.current != null ? serializeEditorContent(editorContentRef.current) : mode === 'edit' && template ? template.text.content : '';
        const payload = {
            type: 'text',
            name: name.trim(),
            pageTitle: pageTitle.trim(),
            displayScope,
            text: {
                content: serializedContent
            }
        };
        if (mode === 'create') {
            createTemplate({
                businessId: businessId !== null && businessId !== void 0 ? businessId : '',
                data: payload
            }, {
                onError: handleSaveError
            });
        } else if (template) {
            updateTemplate({
                businessId: businessId !== null && businessId !== void 0 ? businessId : '',
                templateId: template.id,
                data: payload
            }, {
                onError: handleSaveError
            });
        }
    };
    const handleNameChange = (e)=>{
        setName(e.target.value);
        if (nameError) setNameError(undefined);
    };
    const handlePageTitleChange = (e)=>{
        setPageTitle(e.target.value);
        if (pageTitleError) setPageTitleError(undefined);
    };
    const handleCheckboxChange = ()=>{
        setDisplayScope((prev)=>prev === 'proposalOnly' ? 'proposalAndAgreement' : 'proposalOnly');
    };
    return /*#__PURE__*/ _jsxs(Box, {
        testId: testIds.TextTemplateFormModalTestIds.Root(),
        className: s.formContent,
        children: [
            /*#__PURE__*/ _jsx(TextField, {
                testId: testIds.TextTemplateFormModalTestIds.NameField(),
                label: t.customPages.templates.nameLabel(),
                value: name,
                onChange: handleNameChange,
                error: nameError != null,
                helperText: nameError,
                bi: {
                    name: 'text_template_form_name'
                }
            }),
            /*#__PURE__*/ _jsx(TextField, {
                testId: testIds.TextTemplateFormModalTestIds.PageTitleField(),
                label: t.customPages.templates.pageTitleLabel(),
                value: pageTitle,
                onChange: handlePageTitleChange,
                error: pageTitleError != null,
                helperText: pageTitleError,
                bi: {
                    name: 'text_template_form_page_title'
                }
            }),
            /*#__PURE__*/ _jsx(RichTextEditor, {
                testId: testIds.TextTemplateFormModalTestIds.RichTextEditor(),
                initialValue: initialEditorValue,
                onChange: handleEditorChange,
                editorHeight: 280,
                error: contentError != null,
                helperText: contentError,
                bi: {
                    name: 'text_template_form_editor'
                }
            }),
            /*#__PURE__*/ _jsx(DisplayScopeCheckbox, {
                testId: testIds.TextTemplateFormModalTestIds.DisplayScopeCheckbox(),
                checked: displayScope === 'proposalAndAgreement',
                onChange: handleCheckboxChange
            }),
            /*#__PURE__*/ _jsxs(Row, {
                className: s.formFooter,
                justifyContent: "center",
                gap: 12,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primaryOutline",
                        onClick: onBack,
                        testId: testIds.TextTemplateFormModalTestIds.CancelButton(),
                        bi: {
                            name: 'text_template_form_cancel'
                        },
                        children: t.customPages.templates.cancel()
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primary",
                        loading: isLoading,
                        onClick: handleSave,
                        testId: testIds.TextTemplateFormModalTestIds.SaveButton(),
                        bi: {
                            name: 'text_template_form_save'
                        },
                        children: t.customPages.templates.save()
                    })
                ]
            })
        ]
    });
};
